import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { getFullDate, isCustomerView, linkURl } from 'utils'

import { ExternalLinkIcon } from '@chakra-ui/icons'
import { SimpleGrid, Spacer, Stack } from '@chakra-ui/react'
import { Box, Flex, Icon, Tag, Text, Tooltip } from '@chakra-ui/react'

import { CustomText } from 'components/Misc/CustomText'
import RowComponent from 'components/RowComponent'
import VexStatusComponent from 'components/VulnerabilityVex/VexStatusComponent'

import { usePartsContext } from 'hooks/usePartsContext'
import { useProductUrlContext } from 'hooks/useProductUrlContext'
import { useThemeColor } from 'hooks/useThemeColors'

const ExpandedComponent = (props) => {
  const partsContext = usePartsContext()

  const { data, setActiveRow, onCvssOpen } = props
  const { vuln, fixedVersions, component, lastAffectedVersions } = data
  const { sbom } = component || ''
  const { generateProductVersionDetailPageUrlFromCurrentUrl } =
    useProductUrlContext()

  const { primaryBlueText, primaryTextColor } = useThemeColor([
    'primaryBlueText',
    'primaryTextColor'
  ])

  const customerView = isCustomerView()

  return useMemo(() => {
    const viewCVSS = () => {
      setActiveRow(data)
      onCvssOpen()
    }

    const getDate = (value) => (value ? getFullDate(value) : 'N/A')

    const cvssColor =
      vuln?.cvssVector && !vuln?.cvssVector?.startsWith('[')
        ? primaryBlueText
        : primaryTextColor

    const link = generateProductVersionDetailPageUrlFromCurrentUrl({
      productgroupid: sbom?.project?.projectGroup?.id,
      productid: sbom?.project?.id,
      sbomid: sbom?.id,
      paramsObj: { parts: true, tab: 'components' }
    })

    const handleCheck = () => partsContext.push(sbom)

    return (
      <SimpleGrid
        p={5}
        w={'100%'}
        spacing={10}
        columns={customerView ? 1 : 2}
        boxShadow='inset 0px -5px 5px rgba(0, 0, 0, 0.08), inset 0px 5px 5px rgba(0, 0, 0, 0.08)'
      >
        <Stack spacing={8}>
          {/* Description */}
          <Box w={'90%'}>
            <CustomText>Description :</CustomText>
            <Text mt={1} fontSize={14} color={primaryTextColor}>
              {vuln.desc}
            </Text>
          </Box>
          <SimpleGrid columns={customerView ? 3 : 2} gap={8}>
            {/* Component Name */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>Component Name :</CustomText>
              <Spacer mt={1} />
              {data?.isPart ? (
                <Flex sx={{ mt: 1, gap: 2, alignItems: 'center' }}>
                  <Link to={link} replace>
                    <Icon
                      as={ExternalLinkIcon}
                      onClick={handleCheck}
                      sx={{ w: '16px', h: '16px', color: primaryBlueText }}
                    />
                  </Link>
                  <Text fontSize={14} color={primaryTextColor}>
                    {sbom?.project?.projectGroup?.name || 'N/A'} :{' '}
                    {component?.name || 'N/A'}
                  </Text>
                </Flex>
              ) : customerView ? (
                <Text mt={1} fontSize={14} color={primaryTextColor}>
                  {component?.name}
                </Text>
              ) : (
                <RowComponent content={component} />
              )}
            </Box>
            {/* Component Version */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>Component Version :</CustomText>
              <Text mt={1} fontSize={14} color={primaryTextColor}>
                {component?.version}
              </Text>
            </Box>
            {/* Published At */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>Published:</CustomText>
              <Text mt={1} fontSize={14} color={primaryTextColor}>
                {getDate(vuln?.publishedAt)}
              </Text>
            </Box>
            {/* Last Modified At */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>Last Modified:</CustomText>
              <Text mt={1} fontSize={14} color={primaryTextColor}>
                {getDate(vuln?.lastModifiedAt)}
              </Text>
            </Box>
            {/* Fixed Versions */}
            <Box hidden={customerView}>
              <CustomText>Fixed Version:</CustomText>
              {fixedVersions?.length > 0 ? (
                <Flex mt={1} flexWrap='wrap' alignItems='center' gap={2}>
                  {fixedVersions?.map((item, index) => (
                    <Tag colorScheme='blue' size='sm' key={index}>
                      {item}
                    </Tag>
                  ))}
                </Flex>
              ) : (
                <Text mt={1} fontSize={14} color={primaryTextColor}>
                  N/A
                </Text>
              )}
            </Box>
            {/* CVSS Vector */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>CVSS Vector :</CustomText>
              <Text
                mt={1}
                fontSize={'sm'}
                cursor='pointer'
                color={cvssColor}
                onClick={vuln?.cvssVector ? viewCVSS : null}
              >
                {vuln?.cvssVector || 'N/A'}
              </Text>
            </Box>
            {/* NVD Alias ID */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>NVD Alias ID:</CustomText>
              {vuln.nvdAliasId ? (
                <Flex
                  sx={{ w: 'fit-content', mt: 1, gap: 2, alignItems: 'center' }}
                >
                  <Link to={linkURl('nvd', vuln.nvdAliasId)} target='_blank'>
                    <Icon
                      as={ExternalLinkIcon}
                      color={primaryBlueText}
                      sx={{ w: '16px', h: '16px' }}
                    />
                  </Link>
                  <Tooltip label={vuln.nvdAliasId} placement='top'>
                    <Text
                      sx={{ w: 'fit-content', fontSize: 'sm' }}
                      color={primaryTextColor}
                    >
                      {vuln.nvdAliasId}
                    </Text>
                  </Tooltip>
                </Flex>
              ) : (
                <Text mt={1} fontSize={14} color={primaryTextColor}>
                  N/A
                </Text>
              )}
            </Box>
            {/* EPSS Percentile */}
            <Box flexBasis={customerView ? '30%' : 'auto'}>
              <CustomText>EPSS Percentile :</CustomText>
              <Text mt={1} fontSize={14} color={primaryTextColor}>
                {vuln?.vulnInfo?.epssPercentile
                  ? (vuln?.vulnInfo?.epssPercentile * 100).toFixed()
                  : 0}{' '}
                %
              </Text>
            </Box>
            {/* Last Affected Versions */}
            <Box hidden={customerView}>
              <CustomText>Last Affected Version:</CustomText>
              {lastAffectedVersions?.length > 0 ? (
                <Flex mt={1} flexWrap='wrap' alignItems='center' gap={2}>
                  {lastAffectedVersions?.map((item, index) => (
                    <Tag colorScheme='blue' size='sm' key={index} pt={1}>
                      {item}
                    </Tag>
                  ))}
                </Flex>
              ) : (
                <Text mt={1} fontSize={14} color={primaryTextColor}>
                  N/A
                </Text>
              )}
            </Box>
          </SimpleGrid>
        </Stack>
        {!customerView && <VexStatusComponent data={data} />}
      </SimpleGrid>
    )
  }, [
    vuln?.cvssVector,
    vuln.desc,
    vuln.publishedAt,
    vuln.lastModifiedAt,
    vuln.nvdAliasId,
    vuln?.vulnInfo?.epssPercentile,
    primaryBlueText,
    primaryTextColor,
    generateProductVersionDetailPageUrlFromCurrentUrl,
    sbom,
    customerView,
    data,
    component,
    fixedVersions,
    lastAffectedVersions,
    setActiveRow,
    onCvssOpen,
    partsContext
  ])
}

export default ExpandedComponent
